import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_INVENTORY_URL } from '@/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { RequestApi } from '../models';

const requestsService = createApi({
  reducerPath: 'requestsService',
  baseQuery: createBaseQueryWithReauth(`${API_INVENTORY_URL}/requests`),
  tagTypes: ['Request', 'Requests', 'Assets'] as const,
  endpoints: (builder) => ({
    getRequests: builder.query<
      PaginatedResponse<RequestApi>,
      { paginated: PaginatedRequest; statuses?: string[]; userId?: string; assetTypeId?: string[] }
    >({
      query: ({ paginated, statuses, userId, assetTypeId }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          include: 'admin,user,assignedAsset,assetType',
          ...(statuses &&
            statuses.length > 0 && { status: statuses.join(',') }),
          ...(userId && { userId }),
          ...(assetTypeId &&
            assetTypeId.length > 0 && { assetTypeId: assetTypeId.join(',') }),
        },
      }),
      providesTags: ['Requests'],
    }),
    createRequest: builder.mutation({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Requests'],
    }),
    getRequestById: builder.query<RequestApi, { requestId: string }>({
      query: ({ requestId }) => ({
        url: `/${requestId}`,
        params: {
          include: 'admin,user,assignedAsset,assetType',
        },
      }),
      providesTags: ['Requests'],
    }),
    updateRequest: builder.mutation<
      RequestApi,
      { requestId: string; body: RequestApi }
    >({
      query: ({ requestId, body }) => ({
        url: `/${requestId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Requests'],
    }),
    assignAssetToRequest: builder.mutation<
      RequestApi,
      { requestId: string; assetId: string }
    >({
      query: ({ requestId, assetId }) => ({
        url: `/${requestId}/assign-asset`,
        method: 'PATCH',
        body: { assetId },
      }),
      invalidatesTags: ['Request', 'Requests', 'Assets'],
    }),
  }),
});

export default requestsService;
export const {
  useGetRequestsQuery,
  useCreateRequestMutation,
  useGetRequestByIdQuery,
  useUpdateRequestMutation,
  useAssignAssetToRequestMutation,
} = requestsService;
